/**
 * Tiny typed wrapper over localStorage for the onboarding draft.
 */

import { logger } from './logger';

export const STORAGE_KEY_DRAFT = 'haat_partner_connect_draft';

/**
 * Cap above which we assume the draft is a legacy base64 blob and refuse to
 * parse it (a clean draft is only a few KB). Parsing tens of MB synchronously
 * inside `useState` initialization OOM-freezes Chrome before React can render.
 */
const MAX_RAW_DRAFT_CHARS = 1_000_000;

const removeDraftSafely = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY_DRAFT);
  } catch {
    // ignore - best effort cleanup
  }
};

export const loadDraft = <T>(): T | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_DRAFT);
    if (!raw) return null;

    // Bail out cheaply on legacy / oversized drafts BEFORE JSON.parse so
    // a 30 MB base64 blob can't OOM-freeze the renderer.
    if (raw.length > MAX_RAW_DRAFT_CHARS || raw.includes('"dataUrl"')) {
      removeDraftSafely();
      return null;
    }

    return JSON.parse(raw) as T;
  } catch {
    // Corrupt JSON would otherwise brick every future load.
    removeDraftSafely();
    return null;
  }
};

export const saveDraft = <T>(data: T): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY_DRAFT, JSON.stringify(data));
  } catch (err) {
    // localStorage may be full; fall back silently.
    logger.warn('[HAAT Partner Connect] failed to persist draft', err);
  }
};

export const clearDraft = (): void => {
  if (typeof window === 'undefined') return;
  removeDraftSafely();
};
